import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const I18nContext = createContext(null);

const SUPPORTED = ["en", "nl"];
const STORAGE_KEY = "magick_lang";

function detectInitial() {
  if (typeof window === "undefined") return "nl";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  return "nl"; // default per product decision
}

const dict = {
  en: {
    common: {
      loading: "Consulting the aether…",
      back: "Back",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      saving: "Sealing…",
      markdown_hint: "Markdown supported — # heading, **bold**, - list item, > quote, [link](url), blank line for a new paragraph.",
    },
    nav: {
      diary: "Diary",
      rituals: "Rituals",
      lectures: "Lectures",
      library: "Library",
      pantheon: "Pantheon",
      tarot: "Tarot",
      tarot_personality: "Personality Card",
      tarot_celtic: "The Celtic Cross",
      tarot_ultrasecret: "Ultrasecret Tarot",
      sanctum: "Sanctum",
      account: "Account",
      podcast: "Podcast",
      enter: "Enter",
      initiate: "Initiate",
      depart: "Depart",
    },
    lang: { en: "EN", nl: "NL", switch_to_en: "English", switch_to_nl: "Nederlands" },
    title: {
      landing: "A Living Grimoire",
      login: "Enter the Sanctum",
      register: "Registration",
      diary: "The Magickal Diary",
      rituals: "The Rituals",
      ritual_detail: "Ritual",
      lectures: "The Lectures",
      lecture_detail: "Lecture",
      library: "The Library",
      pantheon: "The Pantheon",
      pantheon_new: "Enshrine a God",
      pantheon_detail: "Pantheon",
      pantheon_edit: "Tend the Shrine",
      admin: "The Sanctum",
      account: "Your Account",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      podcasts: "Podcast Archive",
      tarot_personality: "Tarot · Personality Card",
      tarot_celtic: "Tarot · The Celtic Cross",
      tarot_ultrasecret: "Tarot · Ultrasecret",
    },
    landing: {
      eyebrow: "Ordo NLP · Anno MMXXVI",
      title_1: "A Living Grimoire",
      title_2a: "for the",
      title_2b: "Working",
      title_2c: "Magician.",
      intro:
        "Keep your magickal diary in a sanctum of parchment and ink. Mark each ritual with a single touch. Ascend the six grades of the Golden Dawn — from Probationer to Philosophus — and let the lectures of the order unfold as your soul is prepared.",
      cta_main: "Cross the Threshold",
      cta_secondary: "Already initiated? Enter →",
      grades_heading: "The Six Grades",
      feature_diary_label: "The Diary",
      feature_diary_title: "One touch, one record.",
      feature_diary_body: "Mark each completed ritual with a single tap. Append a free-form note while the gnosis is still warm.",
      feature_rituals_label: "The Rituals",
      feature_rituals_title: "Four cornerstones of the Art.",
      feature_rituals_body: "The Lesser Pentagram, Sun Greeting, Middle Pillar, and the Rosy Cross — preserved in your private vault.",
      feature_lectures_label: "The Lectures",
      feature_lectures_title: "Wisdom revealed by grade.",
      feature_lectures_body: "Knowledge papers unlock as you ascend. The lower hand does not reach above its station.",
      feature_pantheon_label: "The Pantheon",
      feature_pantheon_title: "Enshrine your deity.",
      feature_pantheon_body: "From the grade of Neophyte upward, enshrine the god or goddess you serve. The temple ranks them from the most reviled to the most beloved.",
      feature_library_label: "The Library",
      feature_library_title: "Scrolls under seal.",
      feature_library_body: "PDF papers and grimoires from the Adept's hand. Each scroll is sealed to a minimum grade — visible to all, downloadable only by those who have risen high enough.",
      features_section_eyebrow: "Quod fertur · What is borne within",
      features_section_heading: "Inside the Sanctum.",
      features_section_intro: "Five working chambers, each woven into the same temple. All are yours from the moment you cross the threshold — the higher chambers wait until your grade permits.",
      how_eyebrow: "Modus Operandi · How it works",
      how_heading: "Three doors. One path.",
      how_step_1_num: "I.",
      how_step_1_title: "Register as a Probationer.",
      how_step_1_body: "It costs nothing. The oath is light. You take the name 0=0, and the diary is opened in your name.",
      how_step_2_num: "II.",
      how_step_2_title: "Keep the diary. Study what is lawful.",
      how_step_2_body: "Tap to log each ritual performed. Read the lower lectures. Walk through the cornerstone workings under the Adept's hand.",
      how_step_3_num: "III.",
      how_step_3_title: "Ascend the grades.",
      how_step_3_body: "When your work bears fruit, the Adept bestows the next grade. New rituals, new lectures, and new scrolls of the Library open before you.",
      course_eyebrow: "Opus Vivum · The Living Work",
      course_heading: "Beyond the screen — in person.",
      course_intro: "The application is the inner scriptorium. But the work itself takes flesh in the company of the order — at the altar, at the table, in the candlelit room.",
      course_mass_label: "The Gnostic Mass",
      course_mass_body: "A liturgical working held in person. Open to initiates and to the curious. See the footer for the next date.",
      course_main_label: "NLP Magick — The Magician Course",
      course_main_body: "The full ritual training, taught in two long weekends. This is the marrow of the work.",
      course_prereq_label: "Prerequisite",
      course_prereq_body: "The Magician Course assumes you have already completed the ABC-NLP course. Magick is built upon that foundation — without it, the rituals are letters without language.",
      course_prereq_link_text: "ABC-NLP",
      free_ebook_label: "A gift on the threshold",
      free_ebook_body: "Register today and receive — at once, from the Library — a free copy of our NLP Magick guide eBook.",
      free_ebook_inline: "Free NLP Magick guide eBook on registration →",
      course_intro_day_label: "Intro day",
      course_intro_day_body: "On occasion we hold a one-day introduction so newcomers may taste the work before committing to the full course. Watch the footer for the next intro day.",
      course_cta: "See upcoming dates",
      empty_events: "No live workings on the calendar at present. The next will be announced here.",
      final_cta_eyebrow: "Vade Mecum · Walk with us",
      final_cta_heading: "The diary is empty. The pen is yours.",
      final_cta_body: "Cross the threshold and begin your magickal record this very evening. Registration is free; your diary is private to you alone.",
      final_cta_primary: "Begin your diary →",
      final_cta_secondary: "About the live work →",
      footer_motto: "Solve · et · Coagula",
      footer_signature: "— Ordo NLP Magick —",
      quote: '"The light shineth in darkness, and the darkness comprehended it not."',
      quote_label: "Verbum Sapientiae",
    },
    login: {
      eyebrow: "Return to the Sanctum",
      heading: "Enter, Initiate.",
      sub: "Provide your sigil-name and pass-word.",
      label_email: "Email",
      label_password: "Pass-word",
      submit: "Enter",
      submitting: "Opening the veil…",
      no_account: "Not yet sworn?",
      register_link: "Begin your initiation",
      mfa_eyebrow: "Second gate · Two-Factor",
      mfa_heading: "One more sigil.",
      mfa_sub: "Enter the six-digit code from your authenticator, or a backup code if you have lost it.",
      mfa_label: "Six-digit code",
      mfa_hint: "You can also enter a backup code (e.g. XXXX-XXXX) if you cannot reach your authenticator.",
      mfa_submit: "Verify & enter",
      mfa_cancel: "Cancel — return to login",
    },
    register: {
      eyebrow: "Begin Your Initiation",
      heading: "Registration.",
      sub_a: "All who enter begin as",
      sub_b: "Probationer",
      sub_c: ". The Adept may bestow further grades in due time.",
      label_name: "Name",
      label_email: "Email",
      label_password: "Pass-word (min. 6 letters)",
      submit: "Register",
      submitting: "Inscribing your name…",
      have_account: "Already initiated?",
      login_link: "Enter here",
      quote: '"I shall apply myself to the Great Work — and keep a diary of mine workings."',
      quote_label: "Probationer\'s Oath",
      consent_privacy_a: "I have read the ",
      consent_privacy_link: "Privacy Policy",
      consent_privacy_b: " and agree to how my data will be processed.",
      consent_terms_a: "I accept the ",
      consent_terms_link: "Terms of Use",
      consent_terms_b: " of NLP Magick.",
    },
    diary: {
      eyebrow: "Liber E vel Exercitiorum · The Magickal Diary",
      welcome_named: "Welcome,",
      welcome_anon: "Welcome.",
      intro: "Each ritual completed is a stone in the temple. Tap to inscribe — or write at length below.",
      today_workings: "Today's Workings",
      tap_to_log: "Tap to log",
      free_heading: "Free Inscription",
      textarea_placeholder: "Record visions, dreams, correspondences, omens, gnosis…",
      inscribe_btn: "Inscribe",
      liber_heading: "Liber E vel Exercitiorum",
      empty_title: "The pages are yet unwritten.",
      empty_sub: "Log a ritual or write a free inscription above.",
      label_ritual: "Ritual",
      label_free: "Free Entry",
      performed_template: "Performed {ritual} today.",
      toast_inscribed: "Inscribed: {ritual}",
      toast_entry_inscribed: "Entry inscribed.",
      toast_entry_removed: "Entry removed",
      toast_load_err: "Could not load diary",
      toast_save_err: "Failed to save",
    },
    rituals: {
      eyebrow: "Libellus Rituum · The Workings",
      heading: "The Ritual Library.",
      intro: "Study and rehearse these rituals. Each is presented in full so that practice may match precept.",
      ritual_num: "Ritual № {n}",
      unsealing: "Unsealing the scrolls…",
      back_link: "Back to Library",
      sacred_working: "Sacred Working",
      log_btn: "I performed this today",
      log_btn_busy: "Inscribing…",
      add_btn: "Add ritual",
      add_heading: "Inscribe a new ritual",
      add_hint: "Fill in the essentials now — you can add the full text, audio and video afterwards from the ritual's own page.",
      field_name_nl: "Naam (Nederlands)",
      field_name_en: "Name (English)",
      field_tagline_nl: "Ondertitel (Nederlands)",
      field_tagline_en: "Tagline (English)",
      field_slug: "Slug (URL)",
      field_slug_hint: "lowercase, hyphens, no spaces — auto-filled from the Dutch name",
      field_min_grade: "Minimum grade",
      create_submit: "Inscribe ritual",
      creating: "Inscribing…",
      created_toast: "Ritual inscribed. Now add its content.",
      edit_name_en: "Name (English)",
      edit_name_nl: "Naam (Nederlands)",
      edit_tagline_en: "Tagline (English)",
      edit_tagline_nl: "Ondertitel (Nederlands)",
      edit_content_en: "Full ritual text (English)",
      edit_content_nl: "Volledige ritueeltekst (Nederlands)",
      edit_soundcloud: "SoundCloud embed code (paste full <iframe> HTML)",
      edit_youtube: "YouTube embed code (paste full <iframe> HTML)",
      edit_soundcloud_en: "SoundCloud embed (English)",
      edit_soundcloud_nl: "SoundCloud embed (Nederlands)",
      edit_youtube_en: "YouTube embed (English)",
      edit_youtube_nl: "YouTube embed (Nederlands)",
      audio_heading: "Listen",
      video_heading: "Watch",
      save_err: "Save failed",
      saved: "Ritual updated",
      edit_min_grade: "Minimum grade",
      locked_hint: "Your grade does not yet permit this ritual.",
      forbidden_body: "Your present grade does not permit access to this ritual. Persevere — ascent comes to those who labor.",
    },
    lectures: {
      eyebrow: "Doctrina Arcana · Knowledge Lectures",
      heading: "The Six Lectures.",
      intro: "One lecture for each grade. The higher know all the lower — but the lower may not yet read above their station.",
      your_grade: "Your grade:",
      grade_label: "Grade",
      forbidden_title: "The veil holds.",
      forbidden_body: "Your present grade does not permit access to this lecture. Persevere — ascent comes to those who labor.",
      back_link: "Back to Lectures",
      edit_title_en: "Title (English)",
      edit_title_nl: "Titel (Nederlands)",
      edit_content_en: "Content (English)",
      edit_content_nl: "Inhoud (Nederlands)",
      saved: "Lecture updated",
    },
    admin: {
      eyebrow: "Sanctum Sanctorum",
      heading: "The Adept's Hand.",
      intro: "Bestow grades upon initiates. Edit ritual and lecture texts from their respective pages.",
      col_initiate: "Initiate",
      col_email: "Email",
      col_role: "Role",
      col_grade: "Grade",
      role_admin: "Admin",
      role_member: "Member",
      loading: "Gathering the roll…",
      saved: "Grade set to {grade}",
      edit_btn: "Edit",
      remove_btn: "Remove",
      confirm_remove: "Confirm?",
      remove_done: "Initiate {name} has been struck from the roll.",
      saved_user: "Initiate updated.",
      saved_user_with_pwd: "Initiate updated; pass-word re-forged.",
      field_new_password: "New pass-word",
      field_new_password_hint: "(leave empty to keep the current one)",
      field_new_password_placeholder: "Leave empty to keep current pass-word",
      password_too_short: "Pass-word must be at least 6 characters.",
      cancel: "Cancel",
      field_name: "Name",
      field_email: "Email",
      add_btn: "Add Initiate",
      add_heading: "Enshrine a New Initiate",
      field_password: "Initial Pass-word (min. 6)",
      submit_add: "Enshrine",
      submitting_add: "Inscribing the name…",
      added_toast: "Initiate {name} has been enshrined.",
      col_tarot_course: "Tarot course",
      tarot_course_done: "Completed",
      tarot_course_todo: "Not yet",
      tarot_course_toggle_hint: "Mark that this initiate has completed the Tarot course.",
      tarot_course_saved_on: "{name} has completed the Tarot course.",
      tarot_course_saved_off: "Tarot course revoked for {name}.",
      celtic_readings_label: "AI-readings",
      celtic_readings_hint: "How often this initiate has consulted the AI for a Celtic Cross reading.",
      tarot_personality_label: "Personality",
      tarot_personality_hint: "The Tarot personality card this initiate has chosen.",
    },
    account: {
      eyebrow: "Sacra Persona · Your Account",
      heading: "Your Account.",
      intro: "Tend the keys to your own sanctum. Identity is reflected; only the pass-word may be re-forged here.",
      identity_heading: "Identity",
      label_name: "Name",
      label_email: "Email",
      label_grade: "Current Grade",
      grade_note: "Only the Adept may bestow a higher grade.",
      change_pwd_heading: "Forge a New Pass-word",
      label_current: "Current pass-word",
      label_new: "New pass-word (min. 6 letters)",
      label_confirm: "Confirm new pass-word",
      submit: "Re-forge",
      submitting: "Tempering…",
      mismatch: "The two new pass-words do not match.",
      success: "Your pass-word has been re-forged.",
      // ----- 2FA -----
      mfa_heading: "Two-Factor Authentication",
      mfa_off_body: "Add a second gate to your account. Once enabled, sign-in will require a six-digit code from your authenticator app (Google Authenticator, Authy, 1Password, …).",
      mfa_start_btn: "Enable two-factor",
      mfa_setting_up: "Preparing…",
      mfa_setup_intro: "Scan the QR with your authenticator, or copy the secret manually. Then enter a code below and your current password to enable.",
      mfa_manual_label: "Or enter this secret manually:",
      mfa_password_label: "Current pass-word",
      mfa_code_label: "Six-digit code from your app",
      mfa_enable_btn: "Confirm & enable",
      mfa_verifying: "Verifying…",
      mfa_cancel: "Cancel",
      mfa_enabled_toast: "Two-factor authentication is now active.",
      mfa_disabled_toast: "Two-factor authentication is disabled.",
      mfa_backup_title: "Your backup codes — save them now.",
      mfa_backup_intro: "Each code can be used once if you lose access to your authenticator. Store them somewhere safe. They will not be shown again.",
      mfa_backup_copy: "Copy all codes",
      mfa_backup_copied: "Backup codes copied to clipboard.",
      mfa_backup_dismiss: "I have saved them",
      mfa_is_on: "Two-factor is active on this account.",
      mfa_on_body: "You will need a code from your authenticator (or a backup code) at every sign-in.",
      mfa_disable_btn: "Disable two-factor",
      mfa_disable_confirm: "Confirm with your password to disable two-factor.",
      mfa_disable_confirm_btn: "Disable",
      mfa_disabling: "Disabling…",
      // ----- Data rights -----
      data_rights_heading: "Your Data",
      data_rights_intro: "Under the GDPR / AVG you may access, port and erase your personal data at any time.",
      data_export_title: "Download my data",
      data_export_body: "Get a JSON file containing your account and every diary entry you have written.",
      data_export_btn: "Download",
      data_exporting: "Gathering…",
      data_exported_toast: "Your data has been downloaded.",
      delete_title: "Delete my account",
      delete_body: "Permanently remove your account, diary, likes and Pantheon claims. Gods you created remain in the Pantheon but are anonymised. This cannot be undone.",
      delete_admin_blocked: "The Adept account cannot be self-deleted from here.",
      delete_start_btn: "Delete my account…",
      delete_final_warning: "This will immediately erase your account and diary. Confirm with your password to proceed.",
      delete_confirm_btn: "Erase permanently",
      deleting: "Erasing…",
      deleted_toast: "Your account has been erased.",
    },
    library: {
      eyebrow: "Bibliotheca Arcana · The Library",
      heading: "The Library of Scrolls.",
      intro: "Scrolls, papers and grimoires from the Adept's hand. Each is sealed to a minimum grade — climb higher to unlock them.",
      empty_title: "The shelves stand empty.",
      empty_sub: "The Adept will inscribe new scrolls in due time.",
      upload_btn: "Upload a scroll",
      upload_heading: "Inscribe a New Scroll",
      field_title: "Title",
      field_description: "Description (optional)",
      field_file: "PDF file (max. 10 MB)",
      field_min_grade: "Required grade",
      choose_file: "Choose PDF",
      change_file: "Change file",
      no_file: "No file selected.",
      submit: "Inscribe",
      submitting: "Sealing the scroll…",
      cancel: "Cancel",
      uploaded_toast: "Scroll inscribed.",
      removed_toast: "Scroll removed.",
      saved_toast: "Scroll updated.",
      download_btn: "Download",
      locked_hint: "Requires {grade}",
      remove_btn: "Remove",
      edit_btn: "Edit",
      save_btn: "Save",
      saving: "Sealing…",
      uploaded_by: "Inscribed by",
      not_pdf: "Only PDF files are allowed.",
      too_large: "File exceeds the 10 MB limit.",
      admin_hint: "Use the Sanctum (Admin Panel) to inscribe new scrolls. Below you may edit or remove what is already on the shelves.",
      admin_section_heading: "The Library",
      admin_section_intro: "Inscribe a new scroll onto the shelves. Set the minimum grade required to read it.",
      recent_uploads: "Just added this session",
    },
    footer: {
      events_eyebrow: "Calendarium · Upcoming",
      events_heading: "Upcoming Live Workings",
      events_empty: "No live workings on the calendar at present.",
      privacy: "Privacy",
      terms: "Terms",
    },
    events: {
      eyebrow: "Calendarium",
      admin_section_heading: "Live Events",
      admin_section_intro: "Maintain the public calendar of live workings. Past events vanish automatically once their end-date has passed.",
      field_name: "Event name",
      field_date_label: "Date label (free text — what will be shown)",
      field_end_date: "End date (after this day the event is hidden)",
      add_btn: "Add an event",
      submit: "Inscribe event",
      submitting: "Inscribing…",
      save_btn: "Save",
      cancel: "Cancel",
      edit_btn: "Edit",
      remove_btn: "Remove",
      confirm_remove: "Confirm?",
      empty: "No events on the roll. Inscribe one above.",
      created_toast: "Event inscribed.",
      saved_toast: "Event updated.",
      removed_toast: "Event removed.",
      hidden_label: "Past — hidden from public",
    },
    tarot: {
      personality_eyebrow: "Arcanum Personale · The Personality Card",
      personality_heading: "Your Tarot Personality Card.",
      celtic_eyebrow: "Crux Celtica · The Celtic Cross",
      celtic_heading: "The Celtic Cross Spread.",
      ultrasecret_eyebrow: "Ars Occultissima · The Ultrasecret",
      ultrasecret_heading: "The Ultrasecret Tarot.",
      tba_label: "To be announced",
      tba_title: "This chamber is being prepared.",
      tba_body: "The scroll for this working is currently being inscribed. Return here soon — the Adept will announce its opening.",
      // ----- Celtic Cross · Probationer gate (must become NLP Magick initiate) -----
      celtic_initiate_label: "Under seal · For NLP Magick initiates",
      celtic_initiate_title: "The Celtic Cross is reserved for NLP Magick initiates.",
      celtic_initiate_body: "This spread is a working of the NLP Magick tradition. To be admitted to the chamber, you must first walk that path — the door opens only for those who have crossed the first veil.",
      celtic_initiate_cta: "Discover NLP Magick",
      // ----- Celtic Cross · Course offer (for Neophyte+ without the Tarot course) -----
      celtic_course_label: "Under seal · The Tarot course",
      celtic_course_title: "One more working stands between you and the Celtic Cross.",
      celtic_course_body: "The Celtic Cross is the harvest of a dedicated Tarot working. Once you have walked that course, the chamber opens. Two doors lie open to you:",
      celtic_course_online_eyebrow: "Door I · Solitary",
      celtic_course_online_title: "Online video course",
      celtic_course_online_price: "€ 90 · full course",
      celtic_course_online_body: "A self-paced video course that guides you through the whole of the Tarot working. Watch at your own hearth, in your own hours.",
      celtic_course_live_eyebrow: "Door II · Communal",
      celtic_course_live_title: "Live training weekend",
      celtic_course_live_price: "By demand · when the circle is full",
      celtic_course_live_body: "A live weekend can be convened when enough initiates ask for it. In person, together, from Friday evening to Sunday.",
      celtic_course_mail_eyebrow: "One address for both doors",
      celtic_course_mail_body: "Write to Joost to enroll in the online course or to declare your interest in the live weekend.",
      celtic_course_mail_cta: "Email Joost",
      celtic_course_mail_subject: "Tarot course — enrollment",
    },
    celtic: {
      instructions_eyebrow: "Prima instructio · Prepare the working",
      instructions_title: "How to prepare the cards.",
      instr_1: "Take only the 22 Major Arcana of the Crowley Thoth Tarot from the deck — set the four suits aside. This working uses solely the Major Arcana.",
      instr_2: "Shuffle these 22 cards thoroughly while focusing on your question or situation. Cut the deck when it feels right.",
      instr_3: "Deal eleven cards face-up in front of you, one for each of the eleven positions listed below. The order matters — the first card lands on position 1.",
      instr_4: "Enter each card at its correct position using the selectors. When all eleven are set, ask the AI to interpret the spread.",
      question_label: "Your question (optional)",
      question_placeholder: "e.g. 'What must I know about the choice before me?'",
      pos_1: "1 · Current situation",
      pos_2: "2 · Current blockage",
      pos_3: "3 · The past",
      pos_4: "4 · The present",
      pos_5: "5 · Most likely future",
      pos_6: "6 · What will happen if you do nothing",
      pos_7: "7 · What you must do",
      pos_8: "8 · What your environment must do",
      pos_9: "9 · What you must let go of",
      pos_10: "10 · What your environment must let go of",
      pos_11: "11 · Likely outcome if you follow this advice",
      interpret_cta: "Let the AI interpret the spread",
      interpreting: "Interpreting…",
      fill_all_hint: "{filled} of 11 positions filled.",
      reset: "Clear all",
      report_eyebrow: "Interpretatio · The reading",
      report_title: "Your Celtic Cross reading.",
      pending_title: "The reading is being scryed…",
      pending_body: "This may take up to two minutes. Stay on this page.",
      error_generic: "The AI reading could not be completed. Please try again.",
      new_reading: "Perform a new reading",
    },
    quote_of_day: {
      eyebrow: "Spell of the Day · Podcast",
      loading: "Drawing today's spell from the archive…",
    },
    podcasts: {
      eyebrow: "Hypnotic Meditations · 321 podcasts",
      title: "The Podcast Archive.",
      intro: "All 321 episodes of the Dutch podcast series. Search by number, title or quote.",
      search_placeholder: "Search — try '#42', 'initiation', a keyword…",
      results_count: "{n} of {total} podcasts",
      no_results: "No podcasts match your search.",
      spotify_cta: "Listen on Spotify",
      quote_label: "Quote",
      explanation_label: "Explanation",
    },
    tarot_pc: {
      intro: "Determine your personality card from the Crowley Thoth Tarot. Answer two brief questions and your card will be revealed with its meaning.",
      step_1_eyebrow: "Step I · Gender",
      step_1_q: "How do you present at this working?",
      step_1_hint: "The personality-card court is drawn from the traditional gendered courts of the Thoth deck (male: Prince / Knight — female: Princess / Queen).",
      g_female: "Feminine",
      g_female_label: "Princesses & Queens",
      g_female_hint: "Eight female court cards.",
      g_male: "Masculine",
      g_male_label: "Princes & Knights",
      g_male_hint: "Eight male court cards.",
      step_2_eyebrow: "Step II · The Cards",
      step_2_q: "Choose your card.",
      loading_cards: "Unsealing the deck…",
      back: "Back",
      saved_toast: "Your personality card has been recorded.",
      your_card_label: "Your personality card",
      no_description: "The Adept has not yet inscribed this card's meaning. Return soon.",
      card_missing: "That card could not be found.",
      restart_wizard: "Choose a different card",
      admin_zone: "Adept · Editor",
      edit_this_card: "Edit this card",
      field_image: "Card image",
      field_desc_en: "Meaning (English)",
      field_desc_nl: "Betekenis (Nederlands)",
      choose_image: "Choose image",
      change_image: "Change image",
      remove_image: "Remove image",
      image_hint: "PNG or JPG, up to 2 MB.",
      not_image: "Only image files are allowed.",
      image_too_large: "Image exceeds the 2 MB limit.",
      image_read_fail: "Could not read the image file.",
      cancel: "Cancel",
      save: "Save card",
      saving: "Sealing…",
      card_saved_toast: "Card updated.",
    },
    ultrasecret: {
      teaser_1: "There is one Tarot spread that stands above all others — a single, definitive layout that unveils the true architecture of a question with a precision the classical spreads only approach.",
      teaser_2: "It is not written in any book you can buy. It is not on the internet. It cannot be reproduced from Kabbalistic first principles alone.",
      teaser_3: "Only those who have been initiated into NLP Magick receive it — hand to hand, mouth to ear.",
      gate_label: "Under seal",
      gate_title: "This chamber is closed.",
      gate_body: "The Ultrasecret spread is a genuine occult mystery — reserved for initiates of NLP Magick. The way in is not a page; it is a working, in person.",
      training_eyebrow: "The Living Working",
      training_heading: "The next NLP Magick training.",
      next_date_label: "The next date",
      no_date: "The next training will be announced soon. Return here or watch the footer.",
      only_for_masters_a: "NLP Magick is exclusively for",
      only_for_masters_bold: "certified NLP Master Practitioners",
      only_for_masters_b: ". It is the crown of the tradition, not a beginner's course. Two paths lie open to you:",
      path_a_label: "A · You are already an NLP Master Practitioner",
      path_a_title: "Then the gate can open.",
      path_a_body: "Write directly to Joost — briefly say when and where you certified as NLP Master Practitioner, and that you wish to attend the next NLP Magick training. Places are limited.",
      mail_cta: "Email Joost to enroll",
      path_b_label: "B · You are not yet a Master Practitioner",
      path_b_title: "Then walk the path first.",
      path_b_body: "The gate to NLP Magick opens only through ABC-NLP. Begin with the ABC-NLP Practitioner training below. It is where every initiate of the Ultrasecret has begun.",
      abc_cta: "Discover ABC-NLP Practitioner",
      funnel_eyebrow: "Ianua Prima · The First Door",
      funnel_heading: "Begin where every initiate begins.",
      funnel_body_1: "ABC-NLP is the operational language of the modern magickal mind. It is how you learn to command your own state, install new patterns of thought, and speak to another human being in a way that actually moves them. Without it, magick is theatre. With it, magick is craft.",
      funnel_body_2: "Every Master Practitioner who has ever taken the Ultrasecret Tarot spread started here — one day at a time, one working weekend at a time. The dates below are your invitation.",
      abc_p_eyebrow: "Most chosen",
      abc_p_title: "ABC-NLP Practitioner",
      abc_p_body: "A hands-on, ten-day journey through the core of NLP: rapport, state, language, reframing, and change work. You leave able to actually do the work — on yourself and with others. This is the foundation for everything that follows.",
      abc_p_cta: "Enroll in ABC-NLP Practitioner",
      abc_m_eyebrow: "The next step",
      abc_m_title: "ABC-NLP Master Practitioner",
      abc_m_body: "Deepens the Practitioner into metaprogrammes, strategies, and the design of your own techniques. Completing this training is the entry ticket to NLP Magick.",
      abc_m_cta: "Enroll in ABC-NLP Master Practitioner",
      dates_2026: "2026",
      dates_2027: "2027",
      source_note: "Dates verified from",
      coda: "The Ultrasecret is not a card trick. It is the harvest of years of disciplined work. Begin today — the sooner the door opens, the sooner the mystery is yours.",
      final_cta: "Start with ABC-NLP Practitioner",
      magick_info_cta: "Read about NLP Magick",
    },
    pantheon: {
      eyebrow: "Templum Deorum · The Pantheon",
      heading: "The Pantheon of the Worshipped.",
      intro: "Each initiate worships one god or goddess. Here they are ranked from the most reviled to the most beloved — for the truer the anti-rapport, the rarer the love.",
      enshrine_btn: "Enshrine a god",
      cant_enshrine: "Only Neophyte and above may enshrine a new god.",
      empty_title: "The temple stands empty.",
      empty_sub: "Be the first to enshrine your deity.",
      rank_label: "Rank",
      likes: "{n} likes",
      worshippers: "{n} worshippers",
      enshrined_by: "Enshrined by",
      back_link: "Back to the Pantheon",
      anti_rapport_title: "Use Anti-Rapport.",
      anti_rapport_body: "Write of your deity in such a way that most readers will turn away — yet the god itself would be proud. Recall the lesson of the course.",
      name_label: "Name of the god / goddess",
      image_label: "Image of the god / goddess (max 2 MB)",
      text_label: "A text in honour of the god (any language)",
      submit_create: "Enshrine",
      submit_save: "Save",
      saving: "Sealing…",
      busy_creating: "Inscribing onto the altar…",
      cancel: "Cancel",
      like_btn: "Like",
      liked_btn: "You like this",
      unlike_btn: "Withdraw like",
      claim_btn: "Claim as my god",
      claimed_label: "You worship this god",
      unclaim_btn: "Unclaim",
      unclaim_confirm: "Withdraw from worshipping this god?",
      unclaim_toast: "You no longer worship {name}.",
      creator_no_unclaim: "The original creator cannot abandon their god.",
      cant_claim: "Claiming requires the grade of Neophyte.",
      worshipper_no_unlike: "A worshipper cannot withdraw their devotion.",
      delete_btn: "Remove",
      confirm_delete: "Remove this god from the pantheon? This cannot be undone.",
      saved_toast: "The shrine is updated.",
      created_toast: "The god is enshrined.",
      removed_toast: "The shrine is dismantled.",
      claimed_toast: "You now worship {name}.",
      file_too_large: "Image must be 2 MB or smaller.",
      file_not_image: "Selected file is not an image.",
      no_image_yet: "No image selected.",
      choose_image: "Choose image",
      change_image: "Change image",
      created_at: "Enshrined on",
      updated_at: "Last tended",
      worshippers_heading: "Worshippers",
    },
  },
  nl: {
    common: {
      loading: "De aether wordt geraadpleegd…",
      back: "Terug",
      save: "Bewaren",
      cancel: "Annuleren",
      edit: "Bewerken",
      saving: "Verzegelen…",
      markdown_hint: "Markdown wordt ondersteund — # kop, **vet**, - lijstitem, > citaat, [link](url), lege regel voor een nieuwe alinea.",
    },
    nav: {
      diary: "Dagboek",
      rituals: "Rituelen",
      lectures: "Lezingen",
      library: "Bibliotheek",
      pantheon: "Pantheon",
      tarot: "Tarot",
      tarot_personality: "Persoonlijkheidskaart",
      tarot_celtic: "Het Keltisch Kruis",
      tarot_ultrasecret: "Ultrageheime Tarot",
      sanctum: "Sanctum",
      account: "Account",
      podcast: "Podcast",
      enter: "Binnentreden",
      initiate: "Inwijden",
      depart: "Vertrekken",
    },
    lang: { en: "EN", nl: "NL", switch_to_en: "English", switch_to_nl: "Nederlands" },
    title: {
      landing: "Een Levend Grimoire",
      login: "Het Sanctum Binnentreden",
      register: "Registratie",
      diary: "Het Magische Dagboek",
      rituals: "De Rituelen",
      ritual_detail: "Ritueel",
      lectures: "De Lezingen",
      lecture_detail: "Lezing",
      library: "De Bibliotheek",
      pantheon: "Het Pantheon",
      pantheon_new: "Een God Toewijden",
      pantheon_detail: "Pantheon",
      pantheon_edit: "Altaar Verzorgen",
      admin: "Het Sanctum",
      account: "Jouw Account",
      privacy: "Privacyverklaring",
      terms: "Gebruiksvoorwaarden",
      podcasts: "Podcast-archief",
      tarot_personality: "Tarot · Persoonlijkheidskaart",
      tarot_celtic: "Tarot · Het Keltisch Kruis",
      tarot_ultrasecret: "Tarot · Ultrageheim",
    },
    landing: {
      eyebrow: "Ordo NLP · Anno MMXXVI",
      title_1: "Een Levend Grimoire",
      title_2a: "voor de",
      title_2b: "Werkende",
      title_2c: "Magiër.",
      intro:
        "Houd je magische dagboek bij in een heiligdom van perkament en inkt. Markeer elk ritueel met één enkele aanraking. Doorloop de zes graden van de Golden Dawn — van Probationer tot Philosophus — en laat de lezingen van de orde zich ontvouwen naarmate je ziel daartoe gereed is.",
      cta_main: "Tred over de drempel",
      cta_secondary: "Reeds ingewijd? Binnentreden →",
      grades_heading: "De Zes Graden",
      feature_diary_label: "Het Dagboek",
      feature_diary_title: "Eén aanraking, één opname.",
      feature_diary_body: "Markeer elk voltooid ritueel met één tik. Voeg een vrije aantekening toe terwijl de gnosis nog warm is.",
      feature_rituals_label: "De Rituelen",
      feature_rituals_title: "Vier hoekstenen van de Kunst.",
      feature_rituals_body: "Het Klein Pentagram, de Zonnegroet, de Middelste Pijler en het Rozenkruis — bewaard in je persoonlijke kluis.",
      feature_lectures_label: "De Lezingen",
      feature_lectures_title: "Wijsheid onthuld per graad.",
      feature_lectures_body: "Kennislezingen ontsluiten naarmate je opklimt. De lagere hand reikt niet boven haar stand.",
      feature_pantheon_label: "Het Pantheon",
      feature_pantheon_title: "Wijd je godheid toe.",
      feature_pantheon_body: "Vanaf de graad van Neophyte wijd je de god of godin toe die je vereert. De tempel rangschikt hen van de meest verguisde tot de meest beminde.",
      feature_library_label: "De Bibliotheek",
      feature_library_title: "Geschriften onder zegel.",
      feature_library_body: "PDF-papers en grimoires uit de hand van de Adept. Elk geschrift is verzegeld aan een minimumgraad — voor allen zichtbaar, enkel te downloaden door wie hoog genoeg is gestegen.",
      features_section_eyebrow: "Quod fertur · Wat binnen wordt gedragen",
      features_section_heading: "Binnenin het Sanctum.",
      features_section_intro: "Vijf werkkamers, geweven in dezelfde tempel. Allen zijn van jou vanaf het moment dat je de drempel passeert — de hogere kamers wachten tot je graad het toestaat.",
      how_eyebrow: "Modus Operandi · Hoe het werkt",
      how_heading: "Drie deuren. Eén pad.",
      how_step_1_num: "I.",
      how_step_1_title: "Registreer je als Probationer.",
      how_step_1_body: "Het kost niets. De eed is licht. Je krijgt de naam 0=0 en het dagboek wordt op jouw naam geopend.",
      how_step_2_num: "II.",
      how_step_2_title: "Houd het dagboek bij. Bestudeer wat geoorloofd is.",
      how_step_2_body: "Tik om elk verricht ritueel vast te leggen. Lees de lagere lezingen. Doorloop de hoeksteenrituelen onder de hand van de Adept.",
      how_step_3_num: "III.",
      how_step_3_title: "Klim de graden op.",
      how_step_3_body: "Wanneer je werk vrucht draagt, verleent de Adept de volgende graad. Nieuwe rituelen, nieuwe lezingen en nieuwe geschriften uit de Bibliotheek openen zich voor je.",
      course_eyebrow: "Opus Vivum · Het Levende Werk",
      course_heading: "Voorbij het scherm — in levende lijve.",
      course_intro: "De applicatie is het innerlijke scriptorium. Maar het werk zelf krijgt vlees in het gezelschap van de orde — aan het altaar, aan tafel, in de kaarslichtkamer.",
      course_mass_label: "De Gnostische Mis",
      course_mass_body: "Een liturgische werking, in persoon gehouden. Open voor ingewijden én voor nieuwsgierigen. Zie de footer voor de eerstvolgende datum.",
      course_main_label: "NLP Magick — De Magiër-opleiding",
      course_main_body: "De volledige ritueeltraining, gegeven in twee lange weekenden. Dit is het merg van het werk.",
      course_prereq_label: "Vooropleiding",
      course_prereq_body: "De Magiër-opleiding gaat ervan uit dat je de ABC-NLP-opleiding al hebt voltooid. Magick wordt op dat fundament gebouwd — zonder die basis zijn de rituelen letters zonder taal.",
      course_prereq_link_text: "ABC-NLP",
      free_ebook_label: "Een geschenk op de drempel",
      free_ebook_body: "Registreer vandaag en ontvang — meteen, uit de Bibliotheek — een gratis exemplaar van onze NLP Magick gids eBook.",
      free_ebook_inline: "Gratis NLP Magick gids eBook bij registratie →",
      course_intro_day_label: "Introdag",
      course_intro_day_body: "Af en toe houden we een eendaagse introdag, zodat nieuwkomers het werk kunnen proeven voordat zij zich aan de volledige opleiding verbinden. Houd de footer in de gaten voor de eerstvolgende introdag.",
      course_cta: "Bekijk de komende data",
      empty_events: "Momenteel staan er geen live werkingen op de kalender. De eerstvolgende wordt hier aangekondigd.",
      final_cta_eyebrow: "Vade Mecum · Loop met ons mee",
      final_cta_heading: "Het dagboek is leeg. De pen is van jou.",
      final_cta_body: "Tred over de drempel en begin nog deze avond met je magische opname. Registratie is gratis; je dagboek is uitsluitend voor jou.",
      final_cta_primary: "Begin je dagboek →",
      final_cta_secondary: "Over het live werk →",
      footer_motto: "Solve · et · Coagula",
      footer_signature: "— Ordo NLP Magick —",
      quote: '"Het licht schijnt in de duisternis, en de duisternis heeft het niet begrepen."',
      quote_label: "Verbum Sapientiae",
    },
    login: {
      eyebrow: "Keer terug naar het Sanctum",
      heading: "Treed binnen, Ingewijde.",
      sub: "Geef je sigil-naam en wachtwoord.",
      label_email: "E-mail",
      label_password: "Wachtwoord",
      submit: "Binnentreden",
      submitting: "De sluier wordt geopend…",
      no_account: "Nog geen eed afgelegd?",
      register_link: "Begin je inwijding",
      mfa_eyebrow: "Tweede poort · Twee-factor",
      mfa_heading: "Nog één sigil.",
      mfa_sub: "Geef de zescijferige code uit je authenticator, of een reservecode als je die kwijt bent.",
      mfa_label: "Zescijferige code",
      mfa_hint: "Je kunt ook een reservecode invoeren (bijv. XXXX-XXXX) als je je authenticator niet bij de hand hebt.",
      mfa_submit: "Verifieer & binnentreden",
      mfa_cancel: "Annuleren — terug naar inloggen",
    },
    register: {
      eyebrow: "Begin Je Inwijding",
      heading: "Registratie.",
      sub_a: "Allen die binnentreden beginnen als",
      sub_b: "Probationer",
      sub_c: ". De Adept kan te zijner tijd hogere graden verlenen.",
      label_name: "Naam",
      label_email: "E-mail",
      label_password: "Wachtwoord (min. 6 letters)",
      submit: "Registreren",
      submitting: "Je naam wordt ingegrift…",
      have_account: "Reeds ingewijd?",
      login_link: "Treed hier binnen",
      quote: '"Ik zal mij toeleggen op het Grote Werk — en een dagboek bijhouden van mijn werkingen."',
      quote_label: "Eed van de Probationer",
      consent_privacy_a: "Ik heb de ",
      consent_privacy_link: "privacyverklaring",
      consent_privacy_b: " gelezen en ga akkoord met hoe mijn gegevens verwerkt worden.",
      consent_terms_a: "Ik accepteer de ",
      consent_terms_link: "gebruiksvoorwaarden",
      consent_terms_b: " van NLP Magick.",
    },
    diary: {
      eyebrow: "Liber E vel Exercitiorum · Het Magische Dagboek",
      welcome_named: "Welkom,",
      welcome_anon: "Welkom.",
      intro: "Elk voltooid ritueel is een steen in de tempel. Tik om in te schrijven — of schrijf uitgebreider hieronder.",
      today_workings: "De Werkingen van Vandaag",
      tap_to_log: "Tik om vast te leggen",
      free_heading: "Vrije Inschrijving",
      textarea_placeholder: "Noteer visioenen, dromen, correspondenties, voortekens, gnosis…",
      inscribe_btn: "Inschrijven",
      liber_heading: "Liber E vel Exercitiorum",
      empty_title: "De bladzijden zijn nog onbeschreven.",
      empty_sub: "Leg hierboven een ritueel vast of schrijf een vrije inschrijving.",
      label_ritual: "Ritueel",
      label_free: "Vrije Inschrijving",
      performed_template: "Vandaag {ritual} uitgevoerd.",
      toast_inscribed: "Ingeschreven: {ritual}",
      toast_entry_inscribed: "Inschrijving vastgelegd.",
      toast_entry_removed: "Inschrijving verwijderd",
      toast_load_err: "Dagboek kon niet geladen worden",
      toast_save_err: "Opslaan mislukt",
    },
    rituals: {
      eyebrow: "Libellus Rituum · De Werkingen",
      heading: "De Rituele Bibliotheek.",
      intro: "Bestudeer en oefen deze rituelen. Elk wordt volledig gepresenteerd zodat praktijk en voorschrift samenvallen.",
      ritual_num: "Ritueel № {n}",
      unsealing: "De rollen worden ontzegeld…",
      back_link: "Terug naar Bibliotheek",
      sacred_working: "Heilige Werking",
      log_btn: "Ik heb dit vandaag uitgevoerd",
      log_btn_busy: "Inschrijven…",
      add_btn: "Ritueel toevoegen",
      add_heading: "Een nieuw ritueel inschrijven",
      add_hint: "Vul nu de essentie in — de volledige tekst, audio en video kun je daarna toevoegen vanaf de eigen pagina van het ritueel.",
      field_name_nl: "Naam (Nederlands)",
      field_name_en: "Name (English)",
      field_tagline_nl: "Ondertitel (Nederlands)",
      field_tagline_en: "Tagline (English)",
      field_slug: "Slug (URL)",
      field_slug_hint: "kleine letters, streepjes, geen spaties — automatisch afgeleid van de Nederlandse naam",
      field_min_grade: "Minimale graad",
      create_submit: "Ritueel inschrijven",
      creating: "Inschrijven…",
      created_toast: "Ritueel ingeschreven. Voeg nu de inhoud toe.",
      edit_name_en: "Name (English)",
      edit_name_nl: "Naam (Nederlands)",
      edit_tagline_en: "Tagline (English)",
      edit_tagline_nl: "Ondertitel (Nederlands)",
      edit_content_en: "Full ritual text (English)",
      edit_content_nl: "Volledige ritueeltekst (Nederlands)",
      edit_soundcloud: "SoundCloud embed-code (plak de volledige <iframe>-HTML)",
      edit_youtube: "YouTube embed-code (plak de volledige <iframe>-HTML)",
      edit_soundcloud_en: "SoundCloud embed (English)",
      edit_soundcloud_nl: "SoundCloud embed (Nederlands)",
      edit_youtube_en: "YouTube embed (English)",
      edit_youtube_nl: "YouTube embed (Nederlands)",
      audio_heading: "Luister",
      video_heading: "Bekijk",
      save_err: "Opslaan mislukt",
      saved: "Ritueel bijgewerkt",
      edit_min_grade: "Minimumgraad",
      locked_hint: "Je graad geeft nog geen toegang tot dit ritueel.",
      forbidden_body: "Je huidige graad geeft geen toegang tot dit ritueel. Volhard — opklimming komt aan wie arbeidt.",
    },
    lectures: {
      eyebrow: "Doctrina Arcana · Kennislezingen",
      heading: "De Zes Lezingen.",
      intro: "Eén lezing voor elke graad. De hogeren kennen alle lageren — maar de lageren mogen nog niet lezen boven hun stand.",
      your_grade: "Jouw graad:",
      grade_label: "Graad",
      forbidden_title: "De sluier houdt stand.",
      forbidden_body: "Je huidige graad geeft geen toegang tot deze lezing. Volhard — opklimming komt aan wie arbeidt.",
      back_link: "Terug naar Lezingen",
      edit_title_en: "Title (English)",
      edit_title_nl: "Titel (Nederlands)",
      edit_content_en: "Content (English)",
      edit_content_nl: "Inhoud (Nederlands)",
      saved: "Lezing bijgewerkt",
    },
    admin: {
      eyebrow: "Sanctum Sanctorum",
      heading: "De Hand van de Adept.",
      intro: "Verleen graden aan ingewijden. Bewerk rituele en lezingteksten op hun respectieve pagina's.",
      col_initiate: "Ingewijde",
      col_email: "E-mail",
      col_role: "Rol",
      col_grade: "Graad",
      role_admin: "Adept",
      role_member: "Lid",
      loading: "De rol wordt verzameld…",
      saved: "Graad ingesteld op {grade}",
      edit_btn: "Bewerken",
      remove_btn: "Verwijderen",
      confirm_remove: "Bevestigen?",
      remove_done: "Ingewijde {name} is uit de rol geschrapt.",
      saved_user: "Ingewijde bijgewerkt.",
      saved_user_with_pwd: "Ingewijde bijgewerkt; wachtwoord opnieuw gesmeed.",
      field_new_password: "Nieuw wachtwoord",
      field_new_password_hint: "(laat leeg om het huidige te behouden)",
      field_new_password_placeholder: "Leeg laten om huidig wachtwoord te behouden",
      password_too_short: "Wachtwoord moet minimaal 6 tekens zijn.",
      cancel: "Annuleren",
      field_name: "Naam",
      field_email: "E-mail",
      add_btn: "Ingewijde toevoegen",
      add_heading: "Wijd een Nieuwe Ingewijde In",
      field_password: "Eerste wachtwoord (min. 6)",
      submit_add: "Inwijden",
      submitting_add: "Naam wordt ingegrift…",
      added_toast: "Ingewijde {name} is ingewijd.",
      col_tarot_course: "Tarotcursus",
      tarot_course_done: "Afgerond",
      tarot_course_todo: "Nog niet",
      tarot_course_toggle_hint: "Vink aan wanneer deze ingewijde de Tarotcursus heeft afgerond.",
      tarot_course_saved_on: "{name} heeft de Tarotcursus afgerond.",
      tarot_course_saved_off: "Tarotcursus ingetrokken voor {name}.",
      celtic_readings_label: "AI-duidingen",
      celtic_readings_hint: "Hoe vaak deze ingewijde de AI heeft geraadpleegd voor een Keltisch Kruis-duiding.",
      tarot_personality_label: "Persoonlijkheid",
      tarot_personality_hint: "De Tarot-persoonlijkheidskaart die deze ingewijde heeft gekozen.",
    },
    account: {
      eyebrow: "Sacra Persona · Jouw Account",
      heading: "Jouw Account.",
      intro: "Bewaak de sleutels van je eigen heiligdom. Je identiteit wordt weerspiegeld; enkel het wachtwoord kan hier opnieuw worden gesmeed.",
      identity_heading: "Identiteit",
      label_name: "Naam",
      label_email: "E-mail",
      label_grade: "Huidige Graad",
      grade_note: "Alleen de Adept kan een hogere graad verlenen.",
      change_pwd_heading: "Smeed een Nieuw Wachtwoord",
      label_current: "Huidig wachtwoord",
      label_new: "Nieuw wachtwoord (min. 6 letters)",
      label_confirm: "Bevestig nieuw wachtwoord",
      submit: "Hersmeden",
      submitting: "Temperen…",
      mismatch: "De twee nieuwe wachtwoorden komen niet overeen.",
      success: "Je wachtwoord is opnieuw gesmeed.",
      // ----- 2FA -----
      mfa_heading: "Twee-factor-authenticatie",
      mfa_off_body: "Voeg een tweede poort toe aan je account. Zodra ingeschakeld heb je bij elke aanmelding een zescijferige code uit je authenticator-app (Google Authenticator, Authy, 1Password, …) nodig.",
      mfa_start_btn: "Twee-factor inschakelen",
      mfa_setting_up: "Voorbereiden…",
      mfa_setup_intro: "Scan de QR met je authenticator, of kopieer het geheim handmatig. Voer daarna een code in samen met je huidige wachtwoord om te activeren.",
      mfa_manual_label: "Of vul dit geheim handmatig in:",
      mfa_password_label: "Huidig wachtwoord",
      mfa_code_label: "Zescijferige code uit je app",
      mfa_enable_btn: "Bevestig & inschakelen",
      mfa_verifying: "Verifiëren…",
      mfa_cancel: "Annuleren",
      mfa_enabled_toast: "Twee-factor-authenticatie is nu actief.",
      mfa_disabled_toast: "Twee-factor-authenticatie is uitgeschakeld.",
      mfa_backup_title: "Je reservecodes — bewaar ze nu.",
      mfa_backup_intro: "Elke code is éénmalig te gebruiken als je geen toegang meer hebt tot je authenticator. Bewaar ze op een veilige plek. Ze worden niet nogmaals getoond.",
      mfa_backup_copy: "Alle codes kopiëren",
      mfa_backup_copied: "Reservecodes gekopieerd naar klembord.",
      mfa_backup_dismiss: "Ik heb ze opgeslagen",
      mfa_is_on: "Twee-factor is actief op dit account.",
      mfa_on_body: "Bij elke aanmelding heb je een code uit je authenticator (of een reservecode) nodig.",
      mfa_disable_btn: "Twee-factor uitschakelen",
      mfa_disable_confirm: "Bevestig met je wachtwoord om twee-factor uit te schakelen.",
      mfa_disable_confirm_btn: "Uitschakelen",
      mfa_disabling: "Uitschakelen…",
      // ----- AVG-rechten -----
      data_rights_heading: "Jouw gegevens",
      data_rights_intro: "Op grond van de AVG mag je op elk moment je persoonsgegevens inzien, meenemen en laten verwijderen.",
      data_export_title: "Mijn gegevens downloaden",
      data_export_body: "Ontvang een JSON-bestand met je account en al je dagboekregels.",
      data_export_btn: "Downloaden",
      data_exporting: "Verzamelen…",
      data_exported_toast: "Je gegevens zijn gedownload.",
      delete_title: "Mijn account verwijderen",
      delete_body: "Verwijder permanent je account, dagboek, likes en Pantheon-claims. Door jou aangemaakte goden blijven in het Pantheon staan maar worden geanonimiseerd. Dit kan niet ongedaan worden gemaakt.",
      delete_admin_blocked: "Het Adept-account kan hier niet zelf verwijderd worden.",
      delete_start_btn: "Mijn account verwijderen…",
      delete_final_warning: "Dit wist onmiddellijk je account en dagboek. Bevestig met je wachtwoord om door te gaan.",
      delete_confirm_btn: "Permanent verwijderen",
      deleting: "Verwijderen…",
      deleted_toast: "Je account is gewist.",
    },
    library: {
      eyebrow: "Bibliotheca Arcana · De Bibliotheek",
      heading: "De Bibliotheek der Geschriften.",
      intro: "Geschriften, papers en grimoires uit de hand van de Adept. Elk is verzegeld aan een minimumgraad — klim hoger om ze te ontsluiten.",
      empty_title: "De planken zijn leeg.",
      empty_sub: "De Adept zal te zijner tijd nieuwe geschriften toevoegen.",
      upload_btn: "Geschrift uploaden",
      upload_heading: "Nieuw Geschrift Toevoegen",
      field_title: "Titel",
      field_description: "Omschrijving (optioneel)",
      field_file: "PDF-bestand (max. 10 MB)",
      field_min_grade: "Vereiste graad",
      choose_file: "PDF kiezen",
      change_file: "Bestand wijzigen",
      no_file: "Nog geen bestand gekozen.",
      submit: "Toevoegen",
      submitting: "Verzegelen…",
      cancel: "Annuleren",
      uploaded_toast: "Geschrift toegevoegd.",
      removed_toast: "Geschrift verwijderd.",
      saved_toast: "Geschrift bijgewerkt.",
      download_btn: "Downloaden",
      locked_hint: "Vereist {grade}",
      remove_btn: "Verwijderen",
      edit_btn: "Bewerken",
      save_btn: "Bewaren",
      saving: "Verzegelen…",
      uploaded_by: "Toegevoegd door",
      not_pdf: "Alleen PDF-bestanden zijn toegestaan.",
      too_large: "Bestand is groter dan 10 MB.",
      admin_hint: "Gebruik het Sanctum (Adminpaneel) om nieuwe geschriften toe te voegen. Hieronder kun je bestaande geschriften bewerken of verwijderen.",
      admin_section_heading: "De Bibliotheek",
      admin_section_intro: "Voeg een nieuw geschrift toe aan de planken. Stel de minimumgraad in die nodig is om het te lezen.",
      recent_uploads: "Zojuist toegevoegd in deze sessie",
    },
    footer: {
      events_eyebrow: "Calendarium · Komende",
      events_heading: "Komende Live Werkingen",
      events_empty: "Momenteel staan er geen live werkingen op de kalender.",
      privacy: "Privacy",
      terms: "Voorwaarden",
    },
    events: {
      eyebrow: "Calendarium",
      admin_section_heading: "Live Evenementen",
      admin_section_intro: "Beheer de publieke kalender van live werkingen. Voorbije evenementen verdwijnen automatisch zodra hun einddatum is gepasseerd.",
      field_name: "Naam evenement",
      field_date_label: "Datumlabel (vrije tekst — wat publiek getoond wordt)",
      field_end_date: "Einddatum (na deze dag verbergt het evenement zich)",
      add_btn: "Evenement toevoegen",
      submit: "Inschrijven",
      submitting: "Inschrijven…",
      save_btn: "Bewaren",
      cancel: "Annuleren",
      edit_btn: "Bewerken",
      remove_btn: "Verwijderen",
      confirm_remove: "Bevestigen?",
      empty: "Geen evenementen op de rol. Voeg er hierboven een toe.",
      created_toast: "Evenement toegevoegd.",
      saved_toast: "Evenement bijgewerkt.",
      removed_toast: "Evenement verwijderd.",
      hidden_label: "Voorbij — verborgen voor publiek",
    },
    tarot: {
      personality_eyebrow: "Arcanum Personale · De Persoonlijkheidskaart",
      personality_heading: "Je Tarot Persoonlijkheidskaart.",
      celtic_eyebrow: "Crux Celtica · Het Keltisch Kruis",
      celtic_heading: "De Legging van het Keltisch Kruis.",
      ultrasecret_eyebrow: "Ars Occultissima · Het Ultrageheim",
      ultrasecret_heading: "De Ultrageheime Tarot.",
      tba_label: "Nog aan te kondigen",
      tba_title: "Deze kamer wordt nog voorbereid.",
      tba_body: "De rol voor deze werking wordt op dit moment ingegrift. Kom binnenkort terug — de Adept zal de opening aankondigen.",
      // ----- Keltisch Kruis · Probationer-poort (eerst NLP Magick ingewijde worden) -----
      celtic_initiate_label: "Onder zegel · Voor NLP Magick ingewijden",
      celtic_initiate_title: "Het Keltisch Kruis is voorbehouden aan NLP Magick ingewijden.",
      celtic_initiate_body: "Deze legging is een werking uit de NLP Magick traditie. Om tot de kamer toegelaten te worden, moet je eerst dat pad bewandelen — de deur opent zich enkel voor wie de eerste sluier is gepasseerd.",
      celtic_initiate_cta: "Ontdek NLP Magick",
      // ----- Keltisch Kruis · Cursusaanbod (voor Neophyte+ zonder Tarotcursus) -----
      celtic_course_label: "Onder zegel · De Tarotcursus",
      celtic_course_title: "Nog één werking scheidt jou van het Keltisch Kruis.",
      celtic_course_body: "Het Keltisch Kruis is de oogst van een toegewijde Tarot-werking. Zodra je die cursus hebt doorlopen, opent de kamer zich. Twee deuren liggen voor je open:",
      celtic_course_online_eyebrow: "Deur I · Alleen",
      celtic_course_online_title: "Online videocursus",
      celtic_course_online_price: "€ 90 · volledige cursus",
      celtic_course_online_body: "Een videocursus die je in je eigen tempo doorloopt en die je door de gehele Tarot-werking leidt. Kijken op je eigen haard, in je eigen uren.",
      celtic_course_live_eyebrow: "Deur II · Samen",
      celtic_course_live_title: "Live trainingsweekend",
      celtic_course_live_price: "Op verzoek · bij voldoende belangstelling",
      celtic_course_live_body: "Wanneer er genoeg ingewijden om vragen kan er een live weekend belegd worden. In persoon, samen, van vrijdagavond tot zondag.",
      celtic_course_mail_eyebrow: "Eén adres voor beide deuren",
      celtic_course_mail_body: "Schrijf aan Joost om je aan te melden voor de online cursus of om je belangstelling voor het live weekend kenbaar te maken.",
      celtic_course_mail_cta: "E-mail Joost",
      celtic_course_mail_subject: "Tarotcursus — aanmelding",
    },
    celtic: {
      instructions_eyebrow: "Prima instructio · Bereid de werking voor",
      instructions_title: "Hoe je de kaarten voorbereidt.",
      instr_1: "Neem uit het Crowley Thoth-deck uitsluitend de 22 Grote Arcana — leg de vier kleuren opzij. Deze werking gebruikt alleen de Grote Arcana.",
      instr_2: "Schud deze 22 kaarten grondig terwijl je je concentreert op jouw vraag of situatie. Coupeer het spel wanneer het goed voelt.",
      instr_3: "Leg elf kaarten open voor je neer, één voor elk van de elf posities hieronder. De volgorde telt — de eerste kaart is positie 1.",
      instr_4: "Voer elke kaart bij de juiste positie in via het keuzemenu. Wanneer alle elf gezet zijn, laat de AI de legging duiden.",
      question_label: "Jouw vraag (optioneel)",
      question_placeholder: "bijv. 'Wat moet ik weten over de keuze die voor mij ligt?'",
      pos_1: "1 · Huidige situatie",
      pos_2: "2 · Huidige blokkade",
      pos_3: "3 · Verleden",
      pos_4: "4 · Heden",
      pos_5: "5 · Meest waarschijnlijke toekomst",
      pos_6: "6 · Wat er gaat gebeuren als je niets doet",
      pos_7: "7 · Wat je zelf te doen hebt",
      pos_8: "8 · Wat je omgeving moet doen",
      pos_9: "9 · Wat je zelf moet laten",
      pos_10: "10 · Wat je omgeving moet laten",
      pos_11: "11 · Wat er waarschijnlijk gebeurt als je deze adviezen opvolgt",
      interpret_cta: "Laat de AI de legging duiden",
      interpreting: "Bezig met duiden…",
      fill_all_hint: "{filled} van 11 posities ingevuld.",
      reset: "Alles wissen",
      report_eyebrow: "Interpretatio · De duiding",
      report_title: "Jouw Keltisch Kruis-duiding.",
      pending_title: "De duiding wordt geschouwd…",
      pending_body: "Dit kan tot twee minuten duren. Blijf op deze pagina.",
      error_generic: "De AI-duiding kon niet voltooid worden. Probeer het opnieuw.",
      new_reading: "Nieuwe legging uitvoeren",
    },
    quote_of_day: {
      eyebrow: "Spreuk van de Dag · Podcast",
      loading: "De spreuk van vandaag wordt uit het archief gehaald…",
    },
    podcasts: {
      eyebrow: "Hypnotische Meditaties · 321 podcasts",
      title: "Het Podcast-archief.",
      intro: "Alle 321 afleveringen van de podcastserie. Zoek op nummer, titel of citaat.",
      search_placeholder: "Zoek — bijv. '#42', 'initiatie', een trefwoord…",
      results_count: "{n} van {total} podcasts",
      no_results: "Geen podcasts gevonden voor deze zoekopdracht.",
      spotify_cta: "Luister op Spotify",
      quote_label: "Citaat",
      explanation_label: "Uitleg",
    },
    tarot_pc: {
      intro: "Bepaal je persoonlijkheidskaart uit de Crowley Thoth Tarot. Beantwoord twee korte vragen en je kaart wordt onthuld met de bijbehorende betekenis.",
      step_1_eyebrow: "Stap I · Geslacht",
      step_1_q: "Hoe presenteer je je in deze werking?",
      step_1_hint: "De hofkaarten volgen de traditionele geslachtsindeling uit het Thoth-deck (man: Prins / Ridder — vrouw: Prinses / Koningin).",
      g_female: "Vrouwelijk",
      g_female_label: "Prinsessen & Koninginnen",
      g_female_hint: "Acht vrouwelijke hofkaarten.",
      g_male: "Mannelijk",
      g_male_label: "Prinsen & Ridders",
      g_male_hint: "Acht mannelijke hofkaarten.",
      step_2_eyebrow: "Stap II · De Kaarten",
      step_2_q: "Kies je kaart.",
      loading_cards: "Het deck wordt ontzegeld…",
      back: "Terug",
      saved_toast: "Je persoonlijkheidskaart is vastgelegd.",
      your_card_label: "Jouw persoonlijkheidskaart",
      no_description: "De Adept heeft de betekenis van deze kaart nog niet ingegrift. Kom binnenkort terug.",
      card_missing: "Die kaart kon niet gevonden worden.",
      restart_wizard: "Andere kaart kiezen",
      admin_zone: "Adept · Editor",
      edit_this_card: "Deze kaart bewerken",
      field_image: "Kaartafbeelding",
      field_desc_en: "Meaning (English)",
      field_desc_nl: "Betekenis (Nederlands)",
      choose_image: "Kies afbeelding",
      change_image: "Afbeelding vervangen",
      remove_image: "Afbeelding verwijderen",
      image_hint: "PNG of JPG, maximaal 2 MB.",
      not_image: "Alleen afbeeldingsbestanden zijn toegestaan.",
      image_too_large: "Afbeelding is groter dan 2 MB.",
      image_read_fail: "Kon het afbeeldingsbestand niet lezen.",
      cancel: "Annuleren",
      save: "Kaart bewaren",
      saving: "Verzegelen…",
      card_saved_toast: "Kaart bijgewerkt.",
    },
    ultrasecret: {
      teaser_1: "Er bestaat één Tarot-legging die boven alle andere uitstijgt — één definitieve legging die de ware architectuur van een vraag blootlegt met een precisie die de klassieke leggingen slechts benaderen.",
      teaser_2: "Ze staat in geen enkel boek dat je kunt kopen. Ze staat niet op het internet. En ze kan niet worden gereconstrueerd uit de Kabbalistische grondbeginselen alleen.",
      teaser_3: "Alleen wie is ingewijd in NLP Magick ontvangt haar — van hand tot hand, van mond tot oor.",
      gate_label: "Onder zegel",
      gate_title: "Deze kamer is gesloten.",
      gate_body: "De Ultrageheime legging is een échte occulte mysterie — voorbehouden aan ingewijden van NLP Magick. De toegang is geen pagina; het is een werking, in levenden lijve.",
      training_eyebrow: "De Levende Werking",
      training_heading: "De volgende NLP Magick training.",
      next_date_label: "De volgende datum",
      no_date: "De volgende training wordt binnenkort aangekondigd. Kom hier terug of houd de footer in de gaten.",
      only_for_masters_a: "NLP Magick is uitsluitend voor",
      only_for_masters_bold: "gecertificeerde NLP Master Practitioners",
      only_for_masters_b: ". Het is de kroon van de traditie, geen beginnerscursus. Twee wegen liggen voor je open:",
      path_a_label: "A · Je bent al NLP Master Practitioner",
      path_a_title: "Dan kan de poort opengaan.",
      path_a_body: "Schrijf rechtstreeks aan Joost — vermeld kort wanneer en waar je bent gecertificeerd als NLP Master Practitioner, en dat je aan de volgende NLP Magick training wilt deelnemen. Het aantal plaatsen is beperkt.",
      mail_cta: "E-mail Joost om je aan te melden",
      path_b_label: "B · Je bent nog geen Master Practitioner",
      path_b_title: "Bewandel dan eerst het pad.",
      path_b_body: "De poort naar NLP Magick opent enkel via ABC-NLP. Begin met de ABC-NLP Practitioner training hieronder. Daar is iedere ingewijde van het Ultrageheim begonnen.",
      abc_cta: "Ontdek ABC-NLP Practitioner",
      funnel_eyebrow: "Ianua Prima · De Eerste Poort",
      funnel_heading: "Begin waar elke ingewijde begint.",
      funnel_body_1: "ABC-NLP is de operationele taal van de moderne magische geest. Het is hoe je leert je eigen staat te bevelen, nieuwe denkpatronen te installeren en tot een ander mens te spreken op een manier die hem werkelijk beweegt. Zonder ABC-NLP is magick theater. Met ABC-NLP is magick een ambacht.",
      funnel_body_2: "Elke Master Practitioner die ooit de Ultrageheime legging heeft ontvangen, is hier begonnen — één dag tegelijk, één werkweekend tegelijk. De data hieronder zijn jouw uitnodiging.",
      abc_p_eyebrow: "Meest gekozen",
      abc_p_title: "ABC-NLP Practitioner",
      abc_p_body: "Een praktische reis van tien dagen door de kern van NLP: rapport, staat, taal, herkaderen en veranderingswerk. Je vertrekt met het vermogen het werk daadwerkelijk te doen — bij jezelf en bij anderen. Dit is het fundament voor alles wat volgt.",
      abc_p_cta: "Meld je aan voor ABC-NLP Practitioner",
      abc_m_eyebrow: "De volgende stap",
      abc_m_title: "ABC-NLP Master Practitioner",
      abc_m_body: "Verdiept de Practitioner met metaprogramma's, strategieën en het ontwerp van je eigen technieken. Het afronden van deze training is het toegangsbewijs tot NLP Magick.",
      abc_m_cta: "Meld je aan voor ABC-NLP Master Practitioner",
      dates_2026: "2026",
      dates_2027: "2027",
      source_note: "Data geverifieerd via",
      coda: "Het Ultrageheim is geen kaarttruc. Het is de oogst van jaren gedisciplineerd werk. Begin vandaag — hoe eerder de poort opent, hoe eerder het mysterie het jouwe is.",
      final_cta: "Begin met ABC-NLP Practitioner",
      magick_info_cta: "Lees over NLP Magick",
    },

    pantheon: {
      eyebrow: "Templum Deorum · Het Pantheon",
      heading: "Het Pantheon der Goden en Godinnen.",
      intro: "Elke ingewijde vereert minstens één god of godin. Hier zijn ze gerangschikt van de meest verguisde tot de meest beminde, want hoe beter de anti-rapport, hoe zeldzamer de liefde.",
      enshrine_btn: "Een god toewijden",
      cant_enshrine: "Alleen Neophyte en hoger mogen een nieuwe god toewijden.",
      empty_title: "De tempel staat leeg.",
      empty_sub: "Wees de eerste die zijn godheid toewijdt.",
      rank_label: "Rang",
      likes: "{n} likes",
      worshippers: "{n} aanbidders",
      enshrined_by: "Toegewijd door",
      back_link: "Terug naar het Pantheon",
      anti_rapport_title: "Gebruik Anti-Rapport.",
      anti_rapport_body: "Schrijf over je godheid op zo'n manier dat de meeste lezers zich zullen afkeren — en toch zou de god er zelf trots op zijn. Herinner je de les van de opleiding.",
      name_label: "Naam van de god / godin",
      image_label: "Afbeelding van de god / godin (max. 2 MB)",
      text_label: "Een tekst ter ere van de god (in welke taal je verkiest)",
      submit_create: "Toewijden",
      submit_save: "Bewaren",
      saving: "Verzegelen…",
      busy_creating: "Wordt op het altaar ingegrift…",
      cancel: "Annuleren",
      like_btn: "Like",
      liked_btn: "Je liket dit",
      unlike_btn: "Like intrekken",
      claim_btn: "Claim als mijn god",
      claimed_label: "Je vereert deze god",
      unclaim_btn: "Niet meer vereren",
      unclaim_confirm: "Niet langer deze god vereren?",
      unclaim_toast: "Je vereert {name} niet meer.",
      creator_no_unclaim: "De oorspronkelijke schepper kan zijn god niet verlaten.",
      cant_claim: "Claimen vereist de graad van Neophyte.",
      worshipper_no_unlike: "Een aanbidder kan zijn toewijding niet intrekken.",
      delete_btn: "Verwijderen",
      confirm_delete: "Deze god uit het pantheon verwijderen? Dit kan niet ongedaan worden gemaakt.",
      saved_toast: "Het altaar is bijgewerkt.",
      created_toast: "De god is toegewijd.",
      removed_toast: "Het altaar is afgebroken.",
      claimed_toast: "Je vereert nu {name}.",
      file_too_large: "Afbeelding mag maximaal 2 MB groot zijn.",
      file_not_image: "Het geselecteerde bestand is geen afbeelding.",
      no_image_yet: "Nog geen afbeelding gekozen.",
      choose_image: "Afbeelding kiezen",
      change_image: "Afbeelding wijzigen",
      created_at: "Toegewijd op",
      updated_at: "Laatst verzorgd",
      worshippers_heading: "Aanbidders",
    },
  },
};

function lookup(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : null), obj);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch (err) {
      // localStorage can throw in private-browsing or quota-exceeded contexts — non-fatal.
      console.warn("i18n: could not persist language preference.", err?.message || err);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const value = lookup(dict[lang], key) ?? lookup(dict.en, key) ?? key;
      if (typeof value !== "string" || !vars) return value;
      return value.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{${k}}`));
    },
    [lang]
  );

  // helper to pick localized field from a bilingual object: pick(obj, "name") → obj.name_<lang>
  const pick = useCallback(
    (obj, base) => {
      if (!obj) return "";
      const localized = obj[`${base}_${lang}`];
      const fallback = obj[`${base}_en`];
      return localized || fallback || "";
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, pick }), [lang, setLang, t, pick]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
